import { useState, useMemo, useEffect } from 'react'
import { Activity, BarChart3, RefreshCw, Search, Table2 } from 'lucide-react'
import { formatNumber } from '../../../utils'
import { timeService } from '../../times/services/time'
import type { Time } from '../../times/tipos/time'
import { usePerformance } from '../hooks/usePerformance'
import { obterDatasQuinzenaAnterior } from '../../premiacao/hooks/usePremiacao'
import { useUsuario } from '../../../contexts/UsuarioContext'
import { GraficoColaboradoresDoTime, GraficoTotaisPorTime } from '../componentes/PerformanceGraficos'
import type { PerformanceColaboradorLinha } from '../tipos/performance'

function BarraProgresso({ percentual }: { percentual: number | null | undefined }) {
  if (percentual == null || Number.isNaN(percentual))
    return <span className="text-gray-400 text-xs">—</span>
  const w = Math.min(100, Math.max(0, percentual))
  return (
    <div className="flex items-center gap-2 min-w-[120px]">
      <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
        <div className="h-full bg-brand-500 rounded-full transition-all" style={{ width: `${w}%` }} />
      </div>
      <span className="text-xs text-gray-700 w-10 text-right">{formatNumber(percentual)}%</span>
    </div>
  )
}

function fmtHoras(h: number) {
  return `${formatNumber(h)} h`
}

export default function PerformanceColaboradores() {
  const isAdmin = useUsuario().usuario?.papeis?.includes('1') ?? false
  const defaults = obterDatasQuinzenaAnterior()
  const [dataInicio, setDataInicio] = useState(defaults.dataInicio)
  const [dataFim, setDataFim] = useState(defaults.dataFim)
  const [idTime, setIdTime] = useState<number | ''>('')
  const [times, setTimes] = useState<Time[]>([])
  const [filtroAtivo, setFiltroAtivo] = useState<{
    dataInicio: string
    dataFim: string
    idTime?: number
  }>({ dataInicio: defaults.dataInicio, dataFim: defaults.dataFim })

  const { data, isLoading, refetch } = usePerformance(
    filtroAtivo.dataInicio,
    filtroAtivo.dataFim,
    filtroAtivo.idTime
  )

  useEffect(() => {
    if (!isAdmin) return
    timeService
      .listarTimes()
      .then((list) =>
        setTimes(list.filter((t) => t.descricao?.trim().toLowerCase() !== 'momentum'))
      )
      .catch(() => setTimes([]))
  }, [isAdmin])

  const handleBuscar = () => {
    setFiltroAtivo({
      dataInicio,
      dataFim,
      idTime: idTime === '' ? undefined : Number(idTime),
    })
  }

  const totais = useMemo(() => {
    if (!data?.colaboradores.length)
      return { esp: 0, trab: 0, fat: 0 }
    const c = data.colaboradores
    return {
      esp: c.reduce((a, x) => a + x.minutosEsperados, 0),
      trab: c.reduce((a, x) => a + x.minutosTrabalhadosClockify, 0),
      fat: c.reduce((a, x) => a + x.minutosFaturados, 0),
    }
  }, [data])

  const gruposPorTime = useMemo(() => {
    if (!data?.colaboradores.length)
      return [] as { nomeTime: string; membros: PerformanceColaboradorLinha[] }[]
    const map = new Map<string, PerformanceColaboradorLinha[]>()
    for (const row of data.colaboradores) {
      const key = row.nomeTime?.trim() || 'Sem time'
      if (!map.has(key))
        map.set(key, [])
      map.get(key)!.push(row)
    }
    return [...map.entries()]
      .sort((a, b) => a[0].localeCompare(b[0], 'pt-BR'))
      .map(([nomeTime, membros]) => ({
        nomeTime,
        membros: [...membros].sort((x, y) => x.nome.localeCompare(y.nome, 'pt-BR')),
      }))
  }, [data])

  const dadosTotaisPorTime = useMemo(() => {
    return gruposPorTime.map((g) => {
      const setTk = new Set<string>()
      g.membros.forEach((m) => (m.nomesTickets ?? []).forEach((t) => setTk.add(t)))
      return {
        nomeTime: g.nomeTime,
        horasEsperadas: g.membros.reduce((a, r) => a + r.horasEsperadas, 0),
        horasTrabalhadas: g.membros.reduce((a, r) => a + r.horasTrabalhadas, 0),
        horasFaturadas: g.membros.reduce((a, r) => a + r.horasFaturadas, 0),
        nomesTickets: [...setTk].sort((a, b) => a.localeCompare(b, 'pt-BR')),
      }
    })
  }, [gruposPorTime])

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-gradient-to-br from-brand-500 to-brand-600">
          <Activity className="w-5 h-5 text-white" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Performance</h1>
          <p className="text-sm text-gray-600">
            Gráficos por time: horas esperadas (dias úteis × 8h48), Clockify e minutos com faturamento vinculado
            ao período (cadastro, envio ao analista ou data de faturamento — sem filtrar status do faturamento).
            Tickets exibidos vêm dos apontamentos Clockify no período; tabela opcional abaixo
          </p>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 shadow-theme-sm p-4">
        <div className="flex flex-wrap items-end gap-4">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Data Início</label>
            <input
              type="date"
              value={dataInicio}
              onChange={(e) => setDataInicio(e.target.value)}
              className="rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-700 focus:border-brand-500 focus:ring-brand-500"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Data Fim</label>
            <input
              type="date"
              value={dataFim}
              onChange={(e) => setDataFim(e.target.value)}
              className="rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-700 focus:border-brand-500 focus:ring-brand-500"
            />
          </div>
          {isAdmin && (
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Time</label>
              <select
                value={idTime}
                onChange={(e) => setIdTime(e.target.value === '' ? '' : Number(e.target.value))}
                className="rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-700 focus:border-brand-500 min-w-[200px]"
              >
                <option value="">Todos</option>
                {times.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.descricao}
                  </option>
                ))}
              </select>
            </div>
          )}
          <button
            type="button"
            onClick={handleBuscar}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-brand-500 rounded-lg hover:bg-brand-600"
          >
            <Search className="w-4 h-4" />
            Buscar
          </button>
          <button
            type="button"
            onClick={() => refetch()}
            disabled={isLoading}
            className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {isLoading && !data ? (
        <div className="flex items-center justify-center h-32">
          <RefreshCw className="w-6 h-6 animate-spin text-brand-500" />
        </div>
      ) : data ? (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="bg-white rounded-xl border border-gray-200 shadow-theme-sm p-4">
              <p className="text-xs text-gray-500">Dias úteis no período</p>
              <p className="text-2xl font-semibold text-gray-900">{data.diasUteis}</p>
              <p className="text-xs text-gray-500 mt-1">Segunda a sexta (sem feriados)</p>
            </div>
            <div className="bg-white rounded-xl border border-gray-200 shadow-theme-sm p-4">
              <p className="text-xs text-gray-500">Jornada esperada / colaborador</p>
              <p className="text-2xl font-semibold text-gray-900">
                {fmtHoras(data.minutosEsperadosPorColaborador / 60)}
              </p>
              <p className="text-xs text-gray-500 mt-1">528 min por dia útil</p>
            </div>
            <div className="bg-white rounded-xl border border-gray-200 shadow-theme-sm p-4">
              <p className="text-xs text-gray-500">Total esperado (lista)</p>
              <p className="text-2xl font-semibold text-gray-900">{fmtHoras(totais.esp / 60)}</p>
            </div>
            <div className="bg-white rounded-xl border border-gray-200 shadow-theme-sm p-4">
              <p className="text-xs text-gray-500">Total Clockify / Faturado</p>
              <p className="text-lg font-semibold text-gray-900">
                {fmtHoras(totais.trab / 60)} / {fmtHoras(totais.fat / 60)}
              </p>
            </div>
          </div>

            {data.colaboradores.length > 0 && (
              <>
                <div className="bg-white rounded-xl border border-gray-200 shadow-theme-sm p-4">
                  <div className="flex items-center gap-2 mb-4">
                    <BarChart3 className="w-5 h-5 text-brand-500" />
                    <h2 className="text-lg font-semibold text-gray-900">Totais por time</h2>
                  </div>
                  <p className="text-xs text-gray-500 mb-2">
                    Soma de horas de todos os colaboradores de cada time no período
                  </p>
                  <GraficoTotaisPorTime dados={dadosTotaisPorTime} />
                </div>

                <div className="space-y-6">
                  {gruposPorTime.map((g) => (
                    <div
                      key={g.nomeTime}
                      className="bg-white rounded-xl border border-gray-200 shadow-theme-sm p-4"
                    >
                      <div className="flex flex-wrap items-baseline justify-between gap-2 mb-3">
                        <h3 className="text-base font-semibold text-gray-900">{g.nomeTime}</h3>
                        <span className="text-xs text-gray-500">
                          {g.membros.length} colaborador{g.membros.length === 1 ? '' : 'es'}
                        </span>
                      </div>
                      <GraficoColaboradoresDoTime membros={g.membros} />
                    </div>
                  ))}
                </div>
              </>
            )}

            {data.colaboradores.length === 0 && (
              <div className="bg-white rounded-xl border border-gray-200 shadow-theme-sm p-8 text-center text-gray-500">
                Nenhum colaborador no escopo do filtro.
              </div>
            )}

            {data.colaboradores.length > 0 && (
              <details className="bg-white rounded-xl border border-gray-200 shadow-theme-sm group">
                <summary className="flex cursor-pointer items-center gap-2 px-4 py-3 text-sm font-medium text-gray-800 list-none [&::-webkit-details-marker]:hidden">
                  <Table2 className="w-4 h-4 text-gray-500" />
                  Tabela detalhada (percentuais)
                  <span className="text-xs font-normal text-gray-500 group-open:hidden">— clique para expandir</span>
                </summary>
                <div className="overflow-x-auto border-t border-gray-100 px-0 pb-4">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-gray-200 bg-gray-50">
                        <th className="px-4 py-3 text-left font-semibold text-gray-700">Colaborador</th>
                        <th className="px-4 py-3 text-left font-semibold text-gray-700">Time</th>
                        <th className="px-4 py-3 text-left font-semibold text-gray-700 max-w-xs">
                          Tickets (Clockify)
                        </th>
                        <th className="px-4 py-3 text-right font-semibold text-gray-700">H. esperadas</th>
                        <th className="px-4 py-3 text-right font-semibold text-gray-700">H. Clockify</th>
                        <th className="px-4 py-3 text-right font-semibold text-gray-700">H. faturadas</th>
                        <th className="px-4 py-3 text-left font-semibold text-gray-700">Trab. / esp.</th>
                        <th className="px-4 py-3 text-left font-semibold text-gray-700">Fat. / trab.</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.colaboradores.map((row) => (
                        <tr key={row.pessoaId} className="border-b border-gray-100 hover:bg-gray-50">
                          <td className="px-4 py-3 text-gray-900">{row.nome}</td>
                          <td className="px-4 py-3 text-gray-600">{row.nomeTime ?? '—'}</td>
                          <td className="px-4 py-3 text-xs text-gray-600 max-w-xs align-top">
                            <span className="line-clamp-3" title={(row.nomesTickets ?? []).join(' · ')}>
                              {(row.nomesTickets ?? []).length ? (row.nomesTickets ?? []).join(' · ') : '—'}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-right">{fmtHoras(row.horasEsperadas)}</td>
                          <td className="px-4 py-3 text-right">{fmtHoras(row.horasTrabalhadas)}</td>
                          <td className="px-4 py-3 text-right">{fmtHoras(row.horasFaturadas)}</td>
                          <td className="px-4 py-3">
                            <BarraProgresso percentual={row.percentualTrabalhadoVsEsperado} />
                          </td>
                          <td className="px-4 py-3">
                            <BarraProgresso percentual={row.percentualFaturadoVsTrabalhado} />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </details>
            )}
        </>
      ) : null}
    </div>
  )
}
